import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toDateTimeString } from "../../date_utils";
import {
    equipmentErrorCode,
    equipmentErrorCodes,
    equipmentErrorMessage,
    equipmentErrorTrace,
    getSessionEquipment,
    getSessionEquipmentOptions,
    markSessionEquipmentNotRequired,
    releaseSessionEquipment,
    saveSessionEquipment,
    sessionSetupStatusLabel,
    usageRoleLabel,
} from "../../services/equipment_service";
import { EquipmentSelector } from "./equipment_selector";
import { EquipmentStatusBadge } from "./equipment_status_badge";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
    header: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
    },
    cell: {
        textAlign: "left",
        verticalAlign: "top",
        ...FC.shorthands.padding("6px"),
        borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
    },
    closed: {
        color: FC.tokens.colorNeutralForeground3,
    },
    mono: {
        fontFamily: "Consolas, monospace",
    },
    hint: {
        color: FC.tokens.colorNeutralForeground3,
    },
    dialogBody: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("8px"),
    },
});

const setupStatusColor = {
    pending: "warning",
    configured: "success",
    released: "informative",
    not_required: "subtle",
};

/** Support-facing suffix, empty when the backend did not return one. */
const trace = (failure) => {
    const suffix = equipmentErrorTrace(failure);
    return suffix ? `（${suffix}）` : "";
};

/** The snapshot is the display source; a renamed unit must not rewrite what the job reports. */
const describe = (item) => {
    const snapshot = item.snapshot || {};
    const parts = [snapshot.assetCode, snapshot.model].filter(Boolean);
    return parts.join(" · ") || `#${item.equipmentId}`;
};

/**
 * The "作业器材" area (design 8.6).
 *
 * A whole-set editor rather than per-row add/remove calls: the API applies the kit in one
 * transaction, and driving it with a sequence of requests would reintroduce the half-applied kit
 * that design 6.4 sets out to avoid. The draft below is local until 保存 sends the complete list.
 *
 * Every "may I" decision — edit, release, override an overdue unit — comes from the response and is
 * rendered as given (design 3.6).
 *
 * @param {object} props
 * @param {number} props.sessionId
 * @param {() => void} [props.onChanged] Called after a successful write.
 */
export const SessionEquipmentPanel = ({ sessionId, onChanged }) => {
    const styles = useStyles();

    const [set, setSet] = useState(null);
    const [draft, setDraft] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [picked, setPicked] = useState(null);

    // Raised when the server reports the units are still held elsewhere; resolving it re-sends the
    // very same kit with confirmReassign.
    const [conflicts, setConflicts] = useState(null);
    // Sticky for the current pending save: once the operator confirms a reassignment, a follow-up
    // round (e.g. supplying an overdue-override reason) must keep carrying it, or the server asks to
    // confirm the reassignment a second time. Cleared on a draft change, a cancel, or a saved result.
    const [reassignConfirmed, setReassignConfirmed] = useState(false);
    const [overrideFor, setOverrideFor] = useState(null);
    const [overrideReason, setOverrideReason] = useState("");
    const [notRequiredOpen, setNotRequiredOpen] = useState(false);
    const [notRequiredReason, setNotRequiredReason] = useState("");
    const [correctionReason, setCorrectionReason] = useState("");

    const loadSelectableOptions = useCallback(
        () => getSessionEquipmentOptions(sessionId),
        [sessionId]
    );

    const activeItems = useMemo(() => (set?.items || []).filter((item) => item.isActive), [set]);
    const closedItems = useMemo(() => (set?.items || []).filter((item) => !item.isActive), [set]);

    const resetDraftFrom = useCallback((loaded) => {
        setDraft(
            (loaded?.items || [])
                .filter((item) => item.isActive)
                .map((item) => ({
                    equipmentId: item.equipmentId,
                    usageRole: item.usageRole,
                    label: describe(item),
                    maintenanceOverrideReason: null,
                }))
        );
        setDirty(false);
        setReassignConfirmed(false);
    }, []);

    const load = useCallback(async () => {
        if (!sessionId) {
            return;
        }
        setLoading(true);
        setError("");
        try {
            const loaded = await getSessionEquipment(sessionId);
            setSet(loaded);
            resetDraftFrom(loaded);
        } catch (loadError) {
            console.error("Failed to load session equipment:", loadError);
            // Not an empty kit: reporting "no equipment recorded" for a failed read would have the
            // crew believe the job was never kitted out.
            setSet(null);
            setError(`${equipmentErrorMessage(loadError)}${trace(loadError)}`);
        } finally {
            setLoading(false);
        }
    }, [sessionId, resetDraftFrom]);

    useEffect(() => {
        load();
    }, [load]);

    const applyResult = (updated, message) => {
        setSet(updated);
        resetDraftFrom(updated);
        setNotice(message);
        setError("");
        setConflicts(null);
        setCorrectionReason("");
        onChanged?.();
    };

    const submit = async ({ confirmReassign = reassignConfirmed, items = draft } = {}) => {
        setBusy(true);
        setNotice("");
        try {
            const updated = await saveSessionEquipment(sessionId, {
                items,
                confirmReassign,
                reason: correctionReason || null,
            });
            applyResult(updated, "器材组合已保存。");
        } catch (saveError) {
            const code = equipmentErrorCode(saveError);

            if (code === equipmentErrorCodes.ReassignmentRequiresConfirmation) {
                // A prompt, not a wall (design 4.5): the unit is recorded against another job and
                // the operator decides whether this one takes it over.
                setConflicts(saveError?.details?.conflicts || []);
                setError("");
                return;
            }

            if (code === equipmentErrorCodes.MaintenanceOverrideReasonRequired) {
                const ids = saveError?.details?.equipmentIds || [];
                setOverrideFor(ids.map(Number));
                setOverrideReason("");
                setError("");
                return;
            }

            console.error("Failed to save session equipment:", saveError);
            setError(`${equipmentErrorMessage(saveError)}${trace(saveError)}`);
        } finally {
            setBusy(false);
        }
    };

    const release = async (equipmentIds) => {
        setBusy(true);
        setNotice("");
        try {
            const updated = await releaseSessionEquipment(sessionId, { equipmentIds });
            applyResult(
                updated,
                equipmentIds?.length ? "已结束该器材的现场占用。" : "已结束全部现场占用。"
            );
        } catch (releaseError) {
            console.error("Failed to release session equipment:", releaseError);
            setError(`${equipmentErrorMessage(releaseError)}${trace(releaseError)}`);
        } finally {
            setBusy(false);
        }
    };

    const markNotRequired = async () => {
        setBusy(true);
        setNotice("");
        try {
            const updated = await markSessionEquipmentNotRequired(sessionId, notRequiredReason);
            applyResult(updated, "已标记为无需器材。");
            setNotRequiredOpen(false);
            setNotRequiredReason("");
        } catch (markError) {
            console.error("Failed to mark session as not requiring equipment:", markError);
            setError(`${equipmentErrorMessage(markError)}${trace(markError)}`);
        } finally {
            setBusy(false);
        }
    };

    const addPicked = (option) => {
        if (!option) {
            return;
        }
        if (draft.some((row) => Number(row.equipmentId) === Number(option.id))) {
            setError(`${option.assetCode} 已经在本次作业的器材列表中。`);
            return;
        }
        setDraft((rows) => [
            ...rows,
            {
                equipmentId: option.id,
                usageRole: option.type,
                label: [option.assetCode, option.model].filter(Boolean).join(" · "),
                maintenanceOverrideReason: null,
            },
        ]);
        setDirty(true);
        setReassignConfirmed(false);
        setPicked(null);
        setError("");
    };

    const removeFromDraft = (equipmentId) => {
        setDraft((rows) => rows.filter((row) => Number(row.equipmentId) !== Number(equipmentId)));
        setDirty(true);
        setReassignConfirmed(false);
    };

    const confirmOverride = async () => {
        const ids = new Set(overrideFor || []);
        const items = draft.map((row) =>
            ids.has(Number(row.equipmentId))
                ? { ...row, maintenanceOverrideReason: overrideReason }
                : row
        );
        setDraft(items);
        setOverrideFor(null);
        await submit({ items });
    };

    if (!sessionId) {
        return null;
    }

    const canEdit = Boolean(set?.canEdit);
    const needsCorrectionReason = Boolean(set?.isConcluded && canEdit);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <FC.Subtitle2>作业器材</FC.Subtitle2>
                {set && (
                    <FC.Badge appearance="tint" color={setupStatusColor[set.equipmentSetupStatus] || "subtle"}>
                        {sessionSetupStatusLabel(set.equipmentSetupStatus)}
                    </FC.Badge>
                )}
                {set?.isConcluded && (
                    <FC.Badge appearance="tint" color="subtle">
                        会话已发布
                    </FC.Badge>
                )}
                <div style={{ flex: 1 }} />
                {set?.canRelease && (
                    <FC.Button
                        appearance="secondary"
                        disabled={busy}
                        icon={<IC.ArrowExitRegular />}
                        onClick={() => release(null)}
                    >
                        结束全部占用
                    </FC.Button>
                )}
                {set?.canMarkNotRequired && (
                    <FC.Button appearance="secondary" disabled={busy} onClick={() => setNotRequiredOpen(true)}>
                        标记无需器材
                    </FC.Button>
                )}
                <FC.Button appearance="subtle" icon={<IC.ArrowClockwiseRegular />} onClick={load}>
                    刷新
                </FC.Button>
            </div>

            {set?.equipmentSetupReason && (
                <FC.Caption1 className={styles.hint}>说明：{set.equipmentSetupReason}</FC.Caption1>
            )}

            {error && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>{error}</FC.MessageBarBody>
                </FC.MessageBar>
            )}
            {notice && (
                <FC.MessageBar intent="success">
                    <FC.MessageBarBody>{notice}</FC.MessageBarBody>
                </FC.MessageBar>
            )}

            {canEdit && (
                <div className={styles.header}>
                    <EquipmentSelector
                        // Session-scoped, so the tenant comes from the session rather than from a
                        // prop the task response does not actually carry.
                        loadOptions={loadSelectableOptions}
                        value={picked}
                        disabled={busy}
                        label="添加器材"
                        onChange={(id, option) => {
                            setPicked(id);
                            addPicked(option);
                        }}
                    />
                    {set?.canOverrideMaintenance && (
                        <FC.Caption1 className={styles.hint}>
                            保养逾期的器材需要逐件填写覆盖原因。
                        </FC.Caption1>
                    )}
                </div>
            )}

            {canEdit && (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {["器材", "用途", "操作"].map((label) => (
                                <th className={styles.cell} key={label}>
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {draft.map((row) => (
                            <tr key={row.equipmentId}>
                                <td className={`${styles.cell} ${styles.mono}`}>{row.label}</td>
                                <td className={styles.cell}>{usageRoleLabel(row.usageRole)}</td>
                                <td className={styles.cell}>
                                    <FC.Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<IC.DeleteRegular />}
                                        disabled={busy}
                                        onClick={() => removeFromDraft(row.equipmentId)}
                                    >
                                        移出本次作业
                                    </FC.Button>
                                </td>
                            </tr>
                        ))}
                        {draft.length === 0 && (
                            <tr>
                                <td className={`${styles.cell} ${styles.hint}`} colSpan={3}>
                                    尚未选择器材
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {canEdit && needsCorrectionReason && (
                <FC.Field label="更正原因" required hint="会话已发布，更正器材组合必须填写原因。">
                    <FC.Input
                        value={correctionReason}
                        disabled={busy}
                        onChange={(_, data) => setCorrectionReason(data.value)}
                    />
                </FC.Field>
            )}

            {canEdit && (
                <div className={styles.header}>
                    <FC.Button
                        appearance="primary"
                        disabled={busy || !dirty}
                        onClick={() => {
                            // A fresh save starts a new flow: never inherit a reassignment confirmed
                            // for an earlier, abandoned one. Passed explicitly because the state reset
                            // has not applied yet this tick.
                            setReassignConfirmed(false);
                            submit({ confirmReassign: false });
                        }}
                    >
                        保存器材组合
                    </FC.Button>
                    <FC.Button appearance="secondary" disabled={busy || !dirty} onClick={() => resetDraftFrom(set)}>
                        撤销修改
                    </FC.Button>
                    {dirty && <FC.Caption1 className={styles.hint}>整组保存，未保存的修改不会生效。</FC.Caption1>}
                </div>
            )}

            <FC.Divider>使用记录</FC.Divider>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {["器材（当时快照）", "用途", "开始", "结束", "当前状态", "操作"].map((label) => (
                            <th className={styles.cell} key={label}>
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {[...activeItems, ...closedItems].map((item) => (
                        <tr key={item.id} className={item.isActive ? undefined : styles.closed}>
                            <td className={`${styles.cell} ${styles.mono}`}>
                                {describe(item)}
                                {item.snapshot?.serialNumber && (
                                    <div className={styles.hint}>SN {item.snapshot.serialNumber}</div>
                                )}
                            </td>
                            <td className={styles.cell}>{usageRoleLabel(item.usageRole)}</td>
                            <td className={styles.cell}>{toDateTimeString(item.usedFromUtc)}</td>
                            <td className={styles.cell}>
                                {item.usedToUtc ? toDateTimeString(item.usedToUtc) : "占用中"}
                            </td>
                            <td className={styles.cell}>
                                {/* Only open rows carry live state; a closed one is history and reads
                                    from its snapshot alone. */}
                                {item.current ? (
                                    <EquipmentStatusBadge
                                        kind="operationalStatus"
                                        value={item.current.operationalStatus}
                                    />
                                ) : (
                                    "-"
                                )}
                            </td>
                            <td className={styles.cell}>
                                {item.canRelease && (
                                    <FC.Button
                                        appearance="subtle"
                                        size="small"
                                        disabled={busy}
                                        onClick={() => release([item.equipmentId])}
                                    >
                                        结束占用
                                    </FC.Button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {(set?.items || []).length === 0 && (
                        <tr>
                            <td className={`${styles.cell} ${styles.hint}`} colSpan={6}>
                                {loading ? "加载中..." : "暂无器材使用记录"}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {loading && <FC.Spinner size="tiny" label="加载中..." />}

            <FC.Dialog open={Boolean(conflicts)} onOpenChange={(_, data) => !data.open && setConflicts(null)}>
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>确认改派器材</FC.DialogTitle>
                        <FC.DialogContent className={styles.dialogBody}>
                            <div>以下器材仍记在其他未结束的会话名下，确认要改为本次作业吗？</div>
                            <ul>
                                {(conflicts || []).map((conflict) => (
                                    <li key={`${conflict.equipmentId}-${conflict.sessionId}`}>
                                        {conflict.assetCode} 已记在会话 {conflict.sessionSeq} 名下
                                    </li>
                                ))}
                            </ul>
                            <FC.Caption1 className={styles.hint}>
                                确认后将结束原会话的占用区间并记入审计。
                            </FC.Caption1>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button
                                appearance="secondary"
                                onClick={() => {
                                    setConflicts(null);
                                    setReassignConfirmed(false);
                                }}
                            >
                                取消
                            </FC.Button>
                            <FC.Button
                                appearance="primary"
                                disabled={busy}
                                onClick={() => {
                                    // Confirmed: close this prompt and remember the decision so a
                                    // follow-up overdue-reason round keeps carrying it instead of
                                    // asking to confirm the reassignment a second time.
                                    setConflicts(null);
                                    setReassignConfirmed(true);
                                    submit({ confirmReassign: true });
                                }}
                            >
                                确认改派
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={Boolean(overrideFor)}
                onOpenChange={(_, data) => !data.open && setOverrideFor(null)}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>保养逾期覆盖原因</FC.DialogTitle>
                        <FC.DialogContent className={styles.dialogBody}>
                            <div>所选器材的保养已逾期，管理员覆盖时必须填写原因。</div>
                            <FC.Input
                                value={overrideReason}
                                placeholder="例如：客户窗口期唯一可飞时段，返场后立即保养"
                                onChange={(_, data) => setOverrideReason(data.value)}
                            />
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button
                                appearance="secondary"
                                onClick={() => {
                                    setOverrideFor(null);
                                    setReassignConfirmed(false);
                                }}
                            >
                                取消
                            </FC.Button>
                            <FC.Button
                                appearance="primary"
                                disabled={busy || !overrideReason.trim()}
                                onClick={confirmOverride}
                            >
                                确认覆盖
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={notRequiredOpen}
                onOpenChange={(_, data) => !data.open && setNotRequiredOpen(false)}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>标记为无需器材</FC.DialogTitle>
                        <FC.DialogContent className={styles.dialogBody}>
                            <FC.Input
                                value={notRequiredReason}
                                placeholder="例如：纯资料复核，无现场作业"
                                onChange={(_, data) => setNotRequiredReason(data.value)}
                            />
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setNotRequiredOpen(false)}>
                                取消
                            </FC.Button>
                            <FC.Button
                                appearance="primary"
                                disabled={busy || !notRequiredReason.trim()}
                                onClick={markNotRequired}
                            >
                                确认
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>
        </div>
    );
};
